<template>
    <h1>Blurhash Decode Benchmark</h1>
    <p id="message">{{ message }}</p>
    <p v-if="warning">{{ warning }}</p>
    <div class="upload">
        <div class="slider-container">
            <input type="text" v-model="blurHashRef" placeholder="L6A,LXoD4mO^^nOSM^D%m-yEJ4IT"/>
        </div>
    </div>
    <button id="settings" @click="handleDecode">Decode</button>
    <div class="container">
        <div  @click="handleOriginalClick" class="box"
            :style="{ backgroundImage: `url(${woltBlurHashImage})` }">Wolt BlurHash Decode</div>
        <div @click="handleBlurredlClick" class="box"
            :style="{ backgroundImage: `url(${fastBlurHashImage})` }">Fast BlurHash Decode</div>
    </div>
    <div>
        <div v-if="averageColorTime" class="stats-container">
            <h2>Stats for Nerds:</h2>
            <br>
            <span>{{ woltDecodeTime }}</span>
            <br>
            <span>{{ fastDecodeTime }}</span>
            <br>
            <span>{{ averageColorTime }}</span>
        </div>
    </div>
    <div class="text-container">
        <div class="text-content">

            <h2>Benchmark</h2>
            <p>This benchmark provides a visual and time comparison between the <a href="https://github.com/woltapp/blurhash">Wolt BlurHash Decode Algorithm</a> and the <a href="https://github.com/mad-gooze/fast-blurhash">Fast BlurHash Decode Alogrithm</a>.  It is written in Javascript and is performed client side.  Results will vary depending on hardware specs,
                but should provide a solid reference when deciding between algorithms.  The decode width and height values are 32px by 24px and then scaled up in CSS.  This is arguably the most efficient way to decode, and display a blurhash. Please note that the width and height values in your final implementation
            should be determined by the aspect ratios of the images you are encoding.  32x24 is a good starting place for 16x9 images.  Check out my blurhash encoder <a href="https://blur-ad979.web.app/">here</a>.</p>
            <h2>About</h2>
            <p>Created by <a href="https://github.com/austinginn">Austin Ginn</a>.</p>
        </div>
    </div>
</template>
  
<script>
import { ref, computed } from 'vue';
import { encode, decode, isBlurhashValid } from "blurhash";
import { decodeBlurHash, getBlurHashAverageColor } from 'fast-blurhash';
import Tooltip from '../components/Tooltip.vue';

export default {
    name: 'HomeView',
    components: {
        Tooltip
    },
    setup() {
        const message = ref('Paste a blurhash string to benchmark decode algorithms');
        const canvas = ref(null);
        const warning = ref(null);
        const fastBlurHashImage = ref(null);
        const woltBlurHashImage = ref(null);
        const fastDecodeTime = ref(null);
        const woltDecodeTime = ref(null);
        const averageColorTime = ref(null);
        const blurHashRef = ref("L14CS8xu01NxyDj[RjWW01R*^*xZ");

        let blurHashString = null;
        let rgbAverageColor = null;




        //methods
        const handleDecode = async () => {
            //check input
            if(!blurHashRef.value) {
                warning.value = "No BlurHash provided.";
                return;
            }

            //check if blurhash is valid
            let results = isBlurhashValid(blurHashRef.value);
            console.log(results);
            if(!results.result) {
                warning.value = "BlurHash is not valid.";
                return;
            }

            message.value = "Decoding blur hash with Wolt BlurHash Decode"

            //measure time to decode fast blur hash
            const startWoltDecodeTime = performance.now();
            woltBlurHashImage.value = await showBlur(blurHashRef.value, 32, 24);
            const endWoltDecodeTime = performance.now();
            const resWoltDecodeTime = endWoltDecodeTime - startWoltDecodeTime;
            if (resWoltDecodeTime > 1000) {
                woltDecodeTime.value = `Wolt decode time: ${Math.round((resWoltDecodeTime / 1000) * 100) / 100}s`;
            } else {
                woltDecodeTime.value = `Wolt decode time: ${Math.round(resWoltDecodeTime * 100) / 100}ms`;
            }

            message.value = "Decoding blur hash with Fast BlurHash Decode"
            //measure time to decode fast blur hash
            const startFastDecodeTime = performance.now();
            fastBlurHashImage.value = await showBlurFast(blurHashRef.value, 32, 24);
            const endFastDecodeTime = performance.now();
            const resFastDecodeTime = endFastDecodeTime - startFastDecodeTime;
            if (resFastDecodeTime > 1000) {
                fastDecodeTime.value = `Fast decode time: ${Math.round((resFastDecodeTime / 1000) * 100) / 100}s`;
            } else {
                fastDecodeTime.value = `Fast decode time: ${Math.round(resFastDecodeTime * 100) / 100}ms`;
            }

            message.value = "Done!"
            //reset warnings
            warning.value = null;


            //measure time to get average color from blurhash
            const startColorTime = performance.now();

            //get avg color from blur hash
            rgbAverageColor = getBlurHashAverageColor(blurHashRef.value);

            //measure time to get average color from blurhash
            const endColorTime = performance.now();
            const resColorTime = endColorTime - startColorTime;
            if (resColorTime > 1000) {
                averageColorTime.value = `Color average time: ${Math.round((resColorTime / 1000) * 100) / 100}s`;
            } else {
                averageColorTime.value = `Color average time: ${Math.round(resColorTime * 100) / 100}ms`;
            }

            //set background color of body to average color of blur hash
            document.body.style.transition = 'background-color 1.5s ease-in-out';
            document.body.style.backgroundColor = `rgb(${rgbAverageColor.join(',')})`;


        }

        ///////HELPERS///////
        const showBlur = async (blurHash, width, height) => {
            //decode the hash and get pixels
            const pixels = await decode(blurHash, width, height, 1);

            //generate canvas
            const canvasElement = document.createElement('canvas');
            const context = canvasElement.getContext('2d');

            //set width and height to match original image
            canvasElement.width = width;
            canvasElement.height = height;

            const imageData = context.createImageData(width, height);
            imageData.data.set(pixels);
            context.putImageData(imageData, 0, 0);

            const newDataURL = canvasElement.toDataURL('image/jpeg');

            // // set to new scaled canvas
            // const decodeScaleWidth = Math.floor(width * (100 / 100));
            // const decodeScaleHeight = Math.floor(height * (100 / 100));

            // const newCanvas = document.createElement('canvas');
            // newCanvas.width = decodeScaleWidth;
            // newCanvas.height = decodeScaleHeight;

            // const newContext = newCanvas.getContext('2d');
            // newContext.drawImage(canvasElement, 0, 0, decodeScaleWidth, decodeScaleHeight);

            // const newDataURL = newCanvas.toDataURL('image/jpeg');
            // console.log(newDataURL);

            return newDataURL;
        }

        const showBlurFast = async (blurHash, width, height) => {
            //decode the hash and get pixels
            const pixels = await decodeBlurHash(blurHash, width, height, 1);

            //generate canvas
            const canvasElement = document.createElement('canvas');
            const context = canvasElement.getContext('2d');

            //set width and height to match original image
            canvasElement.width = width;
            canvasElement.height = height;

            const imageData = context.createImageData(width, height);
            imageData.data.set(pixels);
            context.putImageData(imageData, 0, 0);

            const newDataURL = canvasElement.toDataURL('image/jpeg');

            // // set to new scaled canvas
            // const decodeScaleWidth = Math.floor(width * (100 / 100));
            // const decodeScaleHeight = Math.floor(height * (100 / 100));

            // const newCanvas = document.createElement('canvas');
            // newCanvas.width = decodeScaleWidth;
            // newCanvas.height = decodeScaleHeight;

            // const newContext = newCanvas.getContext('2d');
            // newContext.drawImage(canvasElement, 0, 0, decodeScaleWidth, decodeScaleHeight);

            // const newDataURL = newCanvas.toDataURL('image/jpeg');
            // console.log(newDataURL);

            return newDataURL;
        }

        return {
            message,
            warning,
            canvas,
            fastBlurHashImage,
            woltBlurHashImage,
            fastDecodeTime,
            woltDecodeTime,
            averageColorTime,
            blurHashRef,
            handleDecode
        }
    }
}
</script>
<style>
/* Use flexbox to arrange divs side by side on desktop and vertically on mobile */
.container {
    text-align: center;
    padding: 20px;
    padding-top: 0;
    height: 100%;
    /* Set container height to 100% of the viewport height */
    display: flex;
    justify-content: center;
}

h1 {
    margin: 0;
    padding: 1%;
    font-size: 44px;
}

#message {
    margin-top: 0;
}

.box {
    width: 48%;
    /* 2% margin for spacing */
    padding: 3%;
    /* 16x9 aspect ratio padding */
    border-radius: 15px;
    /* Rounded edges */

    /* Background color for the divs */
    background-color: #0f1011;
    margin: 3%;
    margin-top: 0;
    /* margin-bottom: 0; */
    /* Add some margin between divs */
    text-align: center;
    box-sizing: border-box;
    height: 100%;
    /* Set box height to 100% of the container height */
    background-size: cover;
    background-position: center;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

}

button {
    margin: auto;
    /* Center the button horizontally within the .box */
    padding: 10px 20px;
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 15px;
    cursor: pointer;
}

input[type="file"] {
    display: none;
}

/* Media query to stack divs vertically on mobile devices */
@media (max-width: 768px) {
    .container {
        flex-direction: column;
    }

    .text-container {
        width: 100%;
        margin-top: 5%;
        margin: 0 auto;
        text-align: left;
        padding-bottom: 20px;
        /* Full width on mobile */
    }

    .box {
        width: 100%;
        margin: 0;
        margin-top: 5%;
        /* Full width on mobile */

    }
}

@media screen and (min-width: 769px) {

    /* Your styles for screens with a width greater than 768px */
    .text-container {
        width: 50%;
        /* Adjust the width as per your requirement */
        margin: 0 auto;
        text-align: left;
        padding-bottom: 20px;
    }
}

.upload-container {
    text-align: center;
    display: inline-block;
}

#upload {
    margin-top: 10px;
}

.slider-container {
    width: 300px;
    text-align: center;
    /* margin: 0 auto; */
    display: inline-block
}

.slider-container p {
    margin-bottom: 2px;
}

.slider {
    margin-top: 2px;
}

#quality-slider {
    width: 100%;
    /* background: linear-gradient(to right, #f00 0%, #0f0 100%); */
    border: 1px solid #ccc;
    border-radius: 5px;
    -webkit-appearance: none;
    appearance: none;
    height: 10px;
}

#quality-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #0077cc;
    border: 2px solid #fff;
    border-radius: 50%;
    cursor: pointer;
    margin-top: -5px;
}

#slider-value {
    display: block;
    font-size: 1.5em;
    /* margin-top: 10px; */
    color: #0077cc;
}

.action-icons {
    /* display: flex;
    justify-content: space-between; */
    width: 200px;
    margin: 0 auto;
    font-size: 1.5em;
}

.action-button {
    background: none;
    border: none;
    cursor: pointer;
    padding-left: 4px;
}

.action-button i {
    font-size: 24px;
}

.action-button:focus {
    outline: none;
}

#settings {
    margin-top: 10px;
}

.text-content {
    padding: 20px;
}



a {
    color: #0077cc;
}

.slider-container input {
    width: 100%;
    text-align: center;
    background-color: transparent;
    color: white;
    align-items: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
  